import {
  BodyText,
  Button,
  Icons,
  Separator,
  Tooltip,
  toast,
} from "@/components/ui";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { useLogout } from "@/features/Auth/network/useLogout";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "@tanstack/react-router";
import {
  Clipboard,
  Database,
  Inbox,
  LogOut,
  Search,
  Settings,
  Users,
} from "lucide-react";

import { TRACKERS } from "@/constants/Trackers";
import { canAccessAdminPanel } from "@/features/Admin/access";
import { useWaitingRoomCount } from "@/features/LiveQueue/network/useWaitingRoomCount";
import { useAsyncMessageInbox } from "@/features/Messages/network/useAsyncMessageInbox";
import { useTaskList } from "@/features/TaskList/network/useTaskList";
import { useTaskListCount } from "@/features/TaskList/network/useTaskListCount";
import { trackEvent } from "@/lib/tracking";
import { ProfessionalRole, useUser } from "@/network/useUser";
import { ClinicNotificationsPopover } from "./ClinicNotificationsPopover";
import { OnlinePractictionerPopover } from "./OnlinePractictionerPopover";
import { useShiftStatus } from "./network/useShiftStatus";
import { useUpdateShiftStatus } from "./network/useUpdateShiftStatus";

type NavigationItem = {
  icon: React.ElementType;
  route: string;
  label: string;
  badgeText?: number | string;
};

export const Navigation = () => {
  const { pathname } = useLocation();
  const { mutate: logout } = useLogout();
  const { data: shiftData } = useShiftStatus();
  const updateShiftStatus = useUpdateShiftStatus();
  const { data: user } = useUser();

  // TODO: Create an available sections API sometime.
  const canAccessTaskList = [
    ProfessionalRole.LOCAL,
    ProfessionalRole.NURSE_PRACTITIONER,
    ProfessionalRole.ENGINEER,
    ProfessionalRole.MEDICAL_ASSISTANT,
    ProfessionalRole.SUPPORT_AGENT,
  ].includes(user?.professional.role as ProfessionalRole);

  const canAccessInbox = [
    ProfessionalRole.LOCAL,
    ProfessionalRole.NURSE_PRACTITIONER,
    ProfessionalRole.MEDICAL_ASSISTANT,
  ].includes(user?.professional.role as ProfessionalRole);

  const canAccessLiveQueue = [
    ProfessionalRole.LOCAL,
    ProfessionalRole.FOP_COACH,
    ProfessionalRole.NURSE_PRACTITIONER,
    ProfessionalRole.SUPPORT_AGENT,
  ].includes(user?.professional.role as ProfessionalRole);

  const canAccessAdmin = canAccessAdminPanel(user?.professional.role);

  // Use the actual task list data if present from it being loaded elsewhere,
  // otherwise use the count API.
  const { data: taskList } = useTaskList({
    enabled: false,
  });
  const { data: taskListCount } = useTaskListCount(canAccessTaskList);

  // These two don't have count APIs, and it would be just as expensive
  // to fetch the count as it is to fetch the data.
  const { data: inboxData } = useAsyncMessageInbox(canAccessInbox);
  const { data: waitingRoomCount } = useWaitingRoomCount(canAccessLiveQueue);

  const pages: NavigationItem[] = [
    {
      icon: Search,
      route: "/search",
      label: "Search",
      badgeText: undefined,
    },
  ];

  if (canAccessLiveQueue) {
    pages.push({
      icon: Users,
      route: "/live-queue",
      label: "Live",
      badgeText: waitingRoomCount?.waitingRoomCount,
    });
  }

  if (canAccessInbox) {
    pages.push({
      icon: Inbox,
      route: "/messages",
      label: "Inbox",
      badgeText: inboxData?.asyncMessages?.length,
    });
  }

  if (canAccessTaskList) {
    pages.push({
      icon: Clipboard,
      route: "/task-list",
      label: "Task List",
      badgeText:
        taskListCount?.totalTaskCount ||
        taskList?.taskListItems?.length ||
        undefined,
    });
  }

  if (canAccessAdmin) {
    pages.push({
      icon: Database,
      route: "/admin",
      label: "Admin",
      badgeText: undefined,
    });
  }

  const handleClickShift = async () => {
    if (!shiftData?.isOnShift) {
      trackEvent(TRACKERS.TOGGLE_ON_SHIFT);
    } else {
      trackEvent(TRACKERS.TOGGLE_OFF_SHIFT);
    }
    try {
      await updateShiftStatus.mutateAsync({ isOnShift: !shiftData?.isOnShift });
    } catch (error) {
      toast.error("Cannot go off shift with open encounters.");
    }
  };

  const navigationItemClassName =
    "flex items-center justify-center text-white p-2 rounded-lg m-auto w-fit hover:bg-white/25";

  const renderPage = (page: NavigationItem) => {
    const isActive = pathname.includes(page.route);
    const Icon = page.icon;
    return (
      <Tooltip key={page.route} tooltipContentText={page.label} side="right">
        <Link
          key={page.route}
          to={page.route}
          className={cn(navigationItemClassName, isActive ? "bg-white/15" : "")}
          aria-label={page.label}
        >
          <div className="relative">
            {Boolean(page.badgeText) && (
              <div className="absolute w-fit min-w-3 h-3.5 top-[-10px] right-[-12px] bg-button-danger text-white text-xs rounded-[4px] flex items-center justify-center p-0.5 pr-1 pl-1 cursor-pointer">
                <BodyText
                  size="xs"
                  textColor="white"
                  className="cursor-pointer"
                >
                  {page.badgeText}
                </BodyText>
              </div>
            )}
            <Icon
              size={16}
              data-testid={`${page.label.toLowerCase().replace(" ", "-")}-icon`}
            />
          </div>
        </Link>
      </Tooltip>
    );
  };

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="flex flex-col gap-4">
        {renderPage(pages[0])}
        <ClinicNotificationsPopover />
        {pages.slice(1).map(renderPage)}
        <OnlinePractictionerPopover />
      </div>

      <div className="flex flex-col gap-4">
        <Button
          isLoading={updateShiftStatus.isPending}
          onClick={handleClickShift}
          className={cn(
            navigationItemClassName,
            shiftData?.isOnShift
              ? "bg-green-600 hover:bg-green-500"
              : "bg-white/20 text-primary hover:bg-white/40",
          )}
          aria-label="Shift Status"
        >
          <BodyText size="sm" textColor="white" className="cursor-pointer">
            {shiftData?.isOnShift ? "On" : "Off"}
          </BodyText>
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center justify-center text-white p-2 rounded-lg m-auto w-fit hover:bg-white/25"
              aria-label="User Menu"
            >
              <Icons.UserRound size={16} data-testid="user-menu-icon" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-40" align="end" side="right">
            <BodyText className="px-2" size="sm" textColor="secondary">
              {user?.professional.displayNameInternal}
            </BodyText>
            <Separator className="mt-1" />
            <Link
              to="/account-settings"
              className="w-full text-left px-2 py-1.5 mt-2 text-sm hover:bg-muted rounded transition-colors flex items-center gap-2"
            >
              <Settings size={14} />
              <BodyText
                className="cursor-pointer"
                size="sm"
                textColor="secondary"
              >
                Settings
              </BodyText>
            </Link>
            <Link
              to="/action-history"
              className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded transition-colors flex items-center gap-2"
            >
              <Icons.Clock10 size={14} />
              <BodyText
                className="cursor-pointer"
                size="sm"
                textColor="secondary"
              >
                Action History
              </BodyText>
            </Link>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => logout()}
              data-testid="logout-button"
            >
              <span className="flex items-center gap-2">
                <LogOut size={14} />
                Logout
              </span>
            </Button>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
